import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import { ApiError } from '../api/client';
import { clientsApi, exportsApi } from '../api/endpoints';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { ExportMenu } from '../components/ExportMenu';
import { TableSkeleton } from '../components/TableSkeleton';
import { BulkImportDialog } from '../components/BulkImportDialog';
import { ClientFormDialog } from '../features/clients/ClientFormDialog';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../app/ToastProvider';
import { useAuth } from '../app/AuthProvider';
import type { ClientListItem } from '../api/types';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { formatDate, formatPeso } from '../utils/format';

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'PULLED_OUT', label: 'Pulled out' },
];

const COLUMN_COUNT = 13;

export function ClientsPage() {
  const { user } = useAuth();
  const canEditClients = user?.role === 'ORG_ADMIN' || user?.role === 'MANAGER' || user?.role === 'BILLING';
  const canDeleteClients = user?.role === 'ORG_ADMIN' || user?.role === 'MANAGER';
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [lcp, setLcp] = useState('');
  const [nap, setNap] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleting, setDeleting] = useState<ClientListItem | null>(null);
  const [editingDueDate, setEditingDueDate] = useState<ClientListItem | null>(null);
  const [dueDate, setDueDate] = useState('');
  const queryClient = useQueryClient();
  const toast = useToast();
  const deleteMutation = useMutation({
    mutationFn: (id: number) => clientsApi.remove(id),
    onSuccess: async ({ message }) => {
      toast.success(message);
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (caught: unknown) => {
      toast.error(caught instanceof Error ? caught.message : "The client could not be deleted.");
    },
  });
  const dueDateMutation = useMutation({
    mutationFn: () => clientsApi.setDueDate(editingDueDate?.id as number, dueDate),
    onSuccess: async ({ message }) => {
      toast.success(message);
      setEditingDueDate(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['clients'] }),
        queryClient.invalidateQueries({ queryKey: ['client'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
    onError: (caught: unknown) => {
      toast.error(caught instanceof ApiError ? caught.fieldErrors[0]?.message ?? caught.message : 'The due date could not be updated.');
    },
  });

  const openDueDateEditor = (client: ClientListItem): void => {
    setEditingDueDate(client);
    setDueDate(client.nextDueDate?.slice(0, 10) ?? '');
  };

  const status = searchParams.get('status') ?? 'ALL';
  const debouncedSearch = useDebouncedValue(search);

  const queryParams = {
    page: page + 1,
    pageSize,
    search: debouncedSearch || undefined,
    status,
    lcp: lcp.trim() || undefined,
    nap: nap.trim() || undefined,
    sort: 'createdAt',
    order: 'desc' as const,
  };

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['clients', queryParams],
    queryFn: () => clientsApi.list(queryParams),
    placeholderData: keepPreviousData,
  });

  const setStatus = (next: string): void => {
    setPage(0);
    setSearchParams(next === 'ALL' ? {} : { status: next });
  };

  return (
    <>
      <PageHeader
        title="Clients"
        description="Search, review and manage every HiveCore subscriber."
        actions={
          <>
            <ExportMenu
              onExport={(format) =>
                exportsApi.clients(format, { search: debouncedSearch || undefined, status, lcp: lcp.trim() || undefined, nap: nap.trim() || undefined })
              }
              disabled={(data?.items.length ?? 0) === 0}
            />
            {canEditClients ? (
              <>
                <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
                  Bulk Import
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                  Add Client
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Clients could not be loaded.'}
        </Alert>
      ) : null}

      <Card>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ p: 2 }}>
          <TextField
            placeholder="Search by name or client number…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            sx={{ maxWidth: { sm: 380 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />          <TextField
            label="LCP"
            placeholder="e.g. LCP 1"
            value={lcp}
            onChange={(event) => { setLcp(event.target.value); setPage(0); }}
            sx={{ maxWidth: { sm: 160 } }}
          />
          <TextField
            label="NAP"
            placeholder="e.g. NAP 1"
            value={nap}
            onChange={(event) => { setNap(event.target.value); setPage(0); }}
            sx={{ maxWidth: { sm: 160 } }}
          />
          <TextField
            select
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            sx={{ maxWidth: { sm: 200 } }}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Client Number</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Service Plan</TableCell>
                <TableCell>Port</TableCell>
                <TableCell>LCP</TableCell>
                <TableCell>NAP</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Next Due</TableCell>
                <TableCell>Due Status</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Added</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isPending ? (
                <TableSkeleton columns={COLUMN_COUNT} />
              ) : data && data.items.length > 0 ? (
                data.items.map((client) => (
                  <TableRow key={client.id} hover>
                    <TableCell>{client.clientNumber}</TableCell>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/clients/${client.id}`}
                        sx={{ fontWeight: 600 }}
                      >
                        {client.displayName}
                      </Link>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {client.address || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{client.mobileNumber}</TableCell>
                    <TableCell>{client.servicePlanName ?? '—'}</TableCell>
                    <TableCell>{client.portNumber ?? '—'}</TableCell>
                    <TableCell>{client.lcp ?? '—'}</TableCell>
                    <TableCell>{client.nap ?? '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <StatusBadge status={client.subscriptionStatus} />
                        {client.isOverdue ? <StatusBadge status="OVERDUE" /> : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {canEditClients ? (
                        <Button
                          size="small"
                          color="inherit"
                          startIcon={<EditCalendarOutlinedIcon />}
                          disabled={client.subscriptionStatus === null}
                          onClick={() => openDueDateEditor(client)}
                          sx={{ justifyContent: 'flex-start', whiteSpace: 'nowrap' }}
                        >
                          {formatDate(client.nextDueDate)}
                        </Button>
                      ) : formatDate(client.nextDueDate)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={client.daysDue !== null && client.daysDue > 0 ? 'error.main' : 'text.secondary'}>
                        {client.dueDayLabel ?? '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: client.outstandingBalance > 0 ? 'error.main' : 'text.primary',
                        }}
                      >
                        {formatPeso(client.outstandingBalance)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatDate(client.createdAt)}</TableCell>
                    <TableCell align="right">
                      {canDeleteClients ? (
                        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleting(client)}>
                          Delete
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} sx={{ border: 0 }}>
                    <EmptyState
                      message="No clients found."
                      hint="Adjust your search or filters, or add a new client."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={data?.meta.total ?? 0}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete client?"
        description={deleting ? `Delete ${deleting.clientNumber} — ${deleting.displayName}? Clients with billing or operational history cannot be deleted.` : ""}
        confirmLabel="Delete Client"
        danger
        busy={deleteMutation.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
      />
      <Dialog open={editingDueDate !== null} onClose={dueDateMutation.isPending ? undefined : () => setEditingDueDate(null)} maxWidth="xs" fullWidth>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (dueDate) dueDateMutation.mutate();
          }}
        >
          <DialogTitle>Set Due Date</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {editingDueDate ? `${editingDueDate.clientNumber} - ${editingDueDate.displayName}` : ''}
            </Typography>
            <TextField
              autoFocus
              required
              fullWidth
              label="Next Due Date"
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingDueDate(null)} disabled={dueDateMutation.isPending}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={!dueDate || dueDateMutation.isPending}>
              {dueDateMutation.isPending ? 'Saving...' : 'Save Due Date'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <ClientFormDialog open={addOpen} onClose={() => setAddOpen(false)} />
      <BulkImportDialog open={importOpen} initialStep={1} onClose={() => setImportOpen(false)} />
    </>
  );
}
